/**
 * Featured commercial article for BMP Brand Studio.
 * Always rendered above the dynamic InsightsSectionML grid so it cannot
 * be missed. Bilingual-aware (PT / EN minimal copy).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, CheckCircle2, Palette, Type, FileText, Globe, Zap, Award } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import BrandStudioLeadForm from './BrandStudioLeadForm';

const COPY = {
  en: {
    eyebrow: 'Editorial · Brand Studio launch',
    title: 'Why we built BMP Brand Studio — and how it pays for itself in your first month',
    lede:
      "Most businesses spend six to twelve weeks and AED 15-40k on a brand identity. We compressed that into days, at a fraction of the cost — without sacrificing the executive quality our clients expect.",
    advantages_title: 'Six reasons our clients move to BMP Brand Studio',
    advantages: [
      { icon: Zap, h: 'Speed', p: 'A full identity in days, not months. Approve your brand in the same week you brief us.' },
      { icon: Palette, h: 'Executive design', p: 'Curated palettes, real typography pairings, three logo variants — all bound to a master prompt that enforces consistency.' },
      { icon: Type, h: 'A voice, not just a font', p: 'We write your brand voice and 5 tagline candidates. Use them on the site, deck, signature, social.' },
      { icon: FileText, h: 'Brand Book PDF', p: 'An institutional six-section document you can hand to any agency, employee, or partner from day one.' },
      { icon: Globe, h: 'Site included', p: 'A one-page brand website with admin credentials handed over to you. Sell from the moment we deliver.' },
      { icon: Award, h: 'Human-curated', p: 'No drop-and-pray AI. Every output goes through a BMP review gate before it ever reaches you.' },
    ],
    compare_title: 'Compared to a traditional studio',
    compare: [
      { col: 'Traditional studio', items: ['AED 15,000 — 40,000', '6–12 weeks', '1–2 logo concepts', 'Brand book (sometimes)', 'Website extra'], bad: true },
      { col: 'Freelance designer', items: ['AED 5,000 — 12,000', '3–6 weeks', '1 logo concept', 'No brand book', 'No website'], bad: true },
      { col: 'BMP Brand Studio', items: ['From AED 289 / 549 / 1,465', '3–7 days', '3 logo variants', 'Brand Book PDF included', 'One-page website + admin handover'], best: true },
    ],
    plans_title: 'Choose the plan that fits',
    plans: [
      { name: 'Starter', price: 'AED 289', sub: 'Solo founders & landing brands', list: ['Palette + typography', 'Logo (primary)', '1 social mockup'] },
      { name: 'Professional', price: 'AED 549', sub: 'Most popular for new ventures', list: ['Full palette + voice', '3 logo variants', 'Social kit + Brand Book PDF', 'Website handover'], featured: true },
      { name: 'Premium', price: 'AED 1,465', sub: 'Multi-product brands', list: ['Everything in Professional', 'Extended mockup set', 'Priority review queue', 'Revision included'] },
    ],
    most_popular: 'Most popular',
    plan_cta_featured: 'Start with this plan',
    plan_cta_default: 'Choose plan',
    best_value: 'Best value',
    closing_eyebrow: 'Limited 2026 cohort',
    closing_title: 'Stop renting your identity from a freelancer marketplace.',
    closing_body:
      'BMP Brand Studio is the executive way to launch a brand in the GCC and beyond. AI-assisted, human-curated, institutional output.',
    cta_primary: 'Discover Brand Studio',
    cta_secondary: 'Or book a discovery call',
  },
  pt: {
    eyebrow: 'Editorial · Lançamento Brand Studio',
    title: 'Porque construímos o BMP Brand Studio — e como se paga já no primeiro mês',
    lede:
      'A maioria das empresas gasta seis a doze semanas e 15-40k AED numa identidade. Comprimimos isso em dias, a uma fracção do custo — sem comprometer a qualidade executiva que os nossos clientes exigem.',
    advantages_title: 'Seis razões pelas quais os nossos clientes escolhem o BMP Brand Studio',
    advantages: [
      { icon: Zap, h: 'Velocidade', p: 'Identidade completa em dias, não meses. Aprova a marca na mesma semana em que nos briefa.' },
      { icon: Palette, h: 'Design executivo', p: 'Paletas curadas, tipografia real, três variantes de logo — tudo ligado a um master prompt que garante consistência.' },
      { icon: Type, h: 'Uma voz, não só uma fonte', p: 'Escrevemos a voz da marca e 5 candidatos a tagline. Pode usá-los no site, deck, assinatura, redes sociais.' },
      { icon: FileText, h: 'Brand Book PDF', p: 'Documento institucional de seis secções entregue a qualquer agência, colaborador ou parceiro desde o primeiro dia.' },
      { icon: Globe, h: 'Site incluído', p: 'Website de uma página com credenciais admin entregues ao cliente. Pronto a vender no momento da entrega.' },
      { icon: Award, h: 'Curadoria humana', p: 'Nada de "atira IA e reza". Cada output passa por uma porta de revisão BMP antes de chegar ao cliente.' },
    ],
    compare_title: 'Comparado com um estúdio tradicional',
    compare: [
      { col: 'Estúdio tradicional', items: ['15.000 — 40.000 AED', '6–12 semanas', '1–2 conceitos de logo', 'Brand book (às vezes)', 'Website à parte'], bad: true },
      { col: 'Freelancer', items: ['5.000 — 12.000 AED', '3–6 semanas', '1 conceito de logo', 'Sem brand book', 'Sem website'], bad: true },
      { col: 'BMP Brand Studio', items: ['Desde 289 / 549 / 1.465 AED', '3–7 dias', '3 variantes de logo', 'Brand Book PDF incluído', 'Website + handover admin'], best: true },
    ],
    plans_title: 'Escolha o plano certo',
    plans: [
      { name: 'Starter', price: 'AED 289', sub: 'Fundadores solo e landing brands', list: ['Paleta + tipografia', 'Logo (principal)', '1 mockup social'] },
      { name: 'Professional', price: 'AED 549', sub: 'Mais popular para novos negócios', list: ['Paleta completa + voz', '3 variantes de logo', 'Social kit + Brand Book PDF', 'Handover do website'], featured: true },
      { name: 'Premium', price: 'AED 1.465', sub: 'Marcas multi-produto', list: ['Tudo do Professional', 'Mockups estendidos', 'Fila de revisão prioritária', 'Revisão incluída'] },
    ],
    most_popular: 'Mais popular',
    plan_cta_featured: 'Começar com este plano',
    plan_cta_default: 'Escolher plano',
    best_value: 'Melhor relação',
    closing_eyebrow: 'Cohort limitada 2026',
    closing_title: 'Pare de alugar a sua identidade num marketplace de freelancers.',
    closing_body:
      'O BMP Brand Studio é a forma executiva de lançar uma marca no GCC e além. Com IA, curadoria humana e output institucional.',
    cta_primary: 'Descobrir o Brand Studio',
    cta_secondary: 'Ou agende uma reunião',
  },
  fr: {
    eyebrow: 'Éditorial · Lancement Brand Studio',
    title: 'Pourquoi nous avons créé BMP Brand Studio — et comment il se rentabilise dès le premier mois',
    lede:
      "La plupart des entreprises consacrent six à douze semaines et 15-40k AED à leur identité. Nous avons compressé cela en quelques jours, à une fraction du coût — sans sacrifier la qualité exécutive attendue.",
    advantages_title: 'Six raisons pour lesquelles nos clients adoptent BMP Brand Studio',
    advantages: [
      { icon: Zap, h: 'Rapidité', p: 'Une identité complète en jours, pas en mois. Approuvez votre marque dans la semaine du briefing.' },
      { icon: Palette, h: 'Design exécutif', p: 'Palettes choisies, vraies associations typographiques, trois variantes de logo — toutes liées à un prompt maître qui garantit la cohérence.' },
      { icon: Type, h: 'Une voix, pas seulement une police', p: 'Nous écrivons la voix de votre marque et 5 candidats de signature. À utiliser sur le site, les decks, la signature, les réseaux.' },
      { icon: FileText, h: 'Brand Book PDF', p: 'Document institutionnel en six sections, remis à toute agence, employé ou partenaire dès le premier jour.' },
      { icon: Globe, h: 'Site inclus', p: 'Un site one-page avec identifiants admin remis au client. Vendez dès la livraison.' },
      { icon: Award, h: 'Curation humaine', p: "Pas d'IA livrée au hasard. Chaque sortie passe par une revue BMP avant d'atteindre le client." },
    ],
    compare_title: 'Comparé à un studio traditionnel',
    compare: [
      { col: 'Studio traditionnel', items: ['15 000 — 40 000 AED', '6–12 semaines', '1–2 concepts de logo', 'Brand book (parfois)', 'Site en supplément'], bad: true },
      { col: 'Freelance', items: ['5 000 — 12 000 AED', '3–6 semaines', '1 concept de logo', 'Pas de brand book', 'Pas de site'], bad: true },
      { col: 'BMP Brand Studio', items: ['Dès 289 / 549 / 1 465 AED', '3–7 jours', '3 variantes de logo', 'Brand Book PDF inclus', 'Site one-page + remise admin'], best: true },
    ],
    plans_title: 'Choisissez le plan adapté',
    plans: [
      { name: 'Starter', price: 'AED 289', sub: 'Fondateurs solo et lancements', list: ['Palette + typographie', 'Logo (principal)', '1 mockup social'] },
      { name: 'Professional', price: 'AED 549', sub: 'Le plus populaire pour les nouvelles aventures', list: ['Palette complète + voix', '3 variantes de logo', 'Social kit + Brand Book PDF', 'Remise du site'], featured: true },
      { name: 'Premium', price: 'AED 1 465', sub: 'Marques multi-produits', list: ['Tout du Professional', 'Mockups étendus', 'File de revue prioritaire', 'Révision incluse'] },
    ],
    most_popular: 'Plus populaire',
    plan_cta_featured: 'Démarrer avec ce plan',
    plan_cta_default: 'Choisir',
    best_value: 'Meilleur rapport',
    closing_eyebrow: 'Cohorte 2026 limitée',
    closing_title: 'Cessez de louer votre identité sur une marketplace de freelances.',
    closing_body:
      "BMP Brand Studio est la voie exécutive pour lancer une marque dans le GCC et au-delà. Assisté par l'IA, curé par des humains, livré au standard institutionnel.",
    cta_primary: 'Découvrir Brand Studio',
    cta_secondary: 'Ou réserver un rendez-vous',
  },
  ar: {
    eyebrow: 'افتتاحية · إطلاق Brand Studio',
    title: 'لماذا أنشأنا BMP Brand Studio — وكيف يسدد نفسه في الشهر الأول',
    lede:
      'تنفق معظم الشركات ستة إلى اثني عشر أسبوعًا و15-40 ألف درهم على هويتها التجارية. لقد قلصنا ذلك إلى أيام، بجزء بسيط من التكلفة، دون التضحية بالجودة التنفيذية المعتادة.',
    advantages_title: 'ستة أسباب يختار عملاؤنا من أجلها BMP Brand Studio',
    advantages: [
      { icon: Zap, h: 'السرعة', p: 'هوية كاملة في أيام لا في أشهر. اعتمد علامتك التجارية في نفس الأسبوع الذي تقدم فيه المُلخّص.' },
      { icon: Palette, h: 'تصميم تنفيذي', p: 'لوحات ألوان منتقاة، تزاوج طباعي حقيقي، وثلاث صور للشعار — جميعها مرتبطة بـ Prompt رئيسي يفرض التناسق.' },
      { icon: Type, h: 'صوت، لا مجرد خط', p: 'نكتب صوت علامتك التجارية و5 شعارات مقترحة. استخدمها على الموقع، العروض، التوقيع، الشبكات الاجتماعية.' },
      { icon: FileText, h: 'كتاب العلامة PDF', p: 'وثيقة مؤسسية من ست أقسام تُسلِّمها لأي وكالة أو موظف أو شريك من اليوم الأول.' },
      { icon: Globe, h: 'الموقع مشمول', p: 'موقع علامة من صفحة واحدة مع بيانات اعتماد المسؤول. ابدأ البيع لحظة التسليم.' },
      { icon: Award, h: 'إشراف بشري', p: 'لا ذكاء اصطناعي عشوائي. كل مخرج يمر عبر بوابة مراجعة BMP قبل أن يصل إليك.' },
    ],
    compare_title: 'بالمقارنة مع استوديو تقليدي',
    compare: [
      { col: 'استوديو تقليدي', items: ['15,000 — 40,000 درهم', '6–12 أسبوعًا', '1–2 مفهومين للشعار', 'كتاب العلامة (أحيانًا)', 'الموقع إضافي'], bad: true },
      { col: 'مصمم مستقل', items: ['5,000 — 12,000 درهم', '3–6 أسابيع', 'مفهوم شعار واحد', 'لا كتاب علامة', 'لا موقع'], bad: true },
      { col: 'BMP Brand Studio', items: ['اعتبارًا من 289 / 549 / 1,465 درهم', '3–7 أيام', '3 صور للشعار', 'كتاب العلامة PDF مشمول', 'موقع من صفحة + تسليم إداري'], best: true },
    ],
    plans_title: 'اختر الخطة المناسبة',
    plans: [
      { name: 'Starter', price: 'AED 289', sub: 'المؤسسون الفرديون والعلامات الصاعدة', list: ['لوحة ألوان + طباعة', 'الشعار (الأساسي)', 'موك أب اجتماعي واحد'] },
      { name: 'Professional', price: 'AED 549', sub: 'الأكثر شعبية للمشاريع الجديدة', list: ['لوحة ألوان كاملة + صوت', '3 صور للشعار', 'مجموعة اجتماعية + كتاب علامة', 'تسليم الموقع'], featured: true },
      { name: 'Premium', price: 'AED 1,465', sub: 'علامات متعددة المنتجات', list: ['كل ما في Professional', 'مجموعة موك أب موسعة', 'قائمة مراجعة بأولوية', 'مراجعة مشمولة'] },
    ],
    most_popular: 'الأكثر شعبية',
    plan_cta_featured: 'ابدأ بهذه الخطة',
    plan_cta_default: 'اختر الخطة',
    best_value: 'أفضل قيمة',
    closing_eyebrow: 'دفعة 2026 محدودة',
    closing_title: 'توقف عن استئجار هويتك من سوق المستقلين.',
    closing_body:
      'BMP Brand Studio هو الطريقة التنفيذية لإطلاق علامة تجارية في دول الخليج وما بعدها. بمساعدة الذكاء الاصطناعي، وإشراف بشري، وجودة مؤسسية.',
    cta_primary: 'اكتشف Brand Studio',
    cta_secondary: 'أو احجز اجتماعًا تعريفيًا',
  },
  zh: {
    eyebrow: '编辑 · Brand Studio 启动',
    title: '为什么我们打造 BMP Brand Studio — 以及它如何在第一个月就回本',
    lede: '大多数企业要花六到十二周、15-40k AED 来建立品牌身份。我们将其压缩到数天,只需一小部分成本——同时保持客户期待的高管级品质。',
    advantages_title: '六个客户选择 BMP Brand Studio 的理由',
    advantages: [
      { icon: Zap, h: '速度', p: '数天完成完整品牌身份,而不是数月。提交简报当周即可批准品牌。' },
      { icon: Palette, h: '高管级设计', p: '精挑色板、真实字体组合、三种 Logo 变体——全部绑定到统一的主提示词以保证一致性。' },
      { icon: Type, h: '一种声音,不仅是字体', p: '我们撰写品牌声音与 5 个口号候选。可用于网站、提案、签名与社媒。' },
      { icon: FileText, h: 'Brand Book PDF', p: '六个章节的机构级文件,可立即交给任何代理、员工或合作伙伴。' },
      { icon: Globe, h: '网站包含', p: '一页式品牌网站并附带管理员凭证。交付即可开始销售。' },
      { icon: Award, h: '人工策展', p: '不是放任 AI 自由发挥。每个输出在到达客户前都经过 BMP 评审关卡。' },
    ],
    compare_title: '与传统工作室相比',
    compare: [
      { col: '传统工作室', items: ['15,000 — 40,000 AED', '6–12 周', '1–2 个 Logo 概念', '品牌手册(偶尔)', '网站另算'], bad: true },
      { col: '自由设计师', items: ['5,000 — 12,000 AED', '3–6 周', '1 个 Logo 概念', '无品牌手册', '无网站'], bad: true },
      { col: 'BMP Brand Studio', items: ['起价 289 / 549 / 1,465 AED', '3–7 天', '3 种 Logo 变体', '含 Brand Book PDF', '一页网站 + 管理员交接'], best: true },
    ],
    plans_title: '选择合适的方案',
    plans: [
      { name: 'Starter', price: 'AED 289', sub: '独立创始人与初创品牌', list: ['色板 + 字体', '主 Logo', '1 个社媒样张'] },
      { name: 'Professional', price: 'AED 549', sub: '新创业最受欢迎', list: ['完整色板 + 声音', '3 种 Logo 变体', '社媒套件 + Brand Book', '网站交接'], featured: true },
      { name: 'Premium', price: 'AED 1,465', sub: '多产品品牌', list: ['包含 Professional 全部', '扩展样张组合', '优先评审队列', '含一次修订'] },
    ],
    most_popular: '最受欢迎',
    plan_cta_featured: '选择此方案',
    plan_cta_default: '选择方案',
    best_value: '性价比最优',
    closing_eyebrow: '2026 限量名额',
    closing_title: '不要再把你的品牌身份外包给自由职业平台。',
    closing_body:
      'BMP Brand Studio 是在 GCC 及全球启动品牌的高管级方式。AI 辅助、人工策展、机构级输出。',
    cta_primary: '探索 Brand Studio',
    cta_secondary: '或预约一次诊断电话',
  },
};

const FeaturedBrandStudioArticle = () => {
  const { language } = useLanguage();
  const c = COPY[language] || COPY.en;

  return (
    <section className="py-20 bg-[#F8FAFC]" data-testid="insights-brand-studio-feature">
      <div className="container mx-auto px-6 max-w-6xl">
        {/* Editorial header */}
        <div className="flex items-center gap-3 mb-4">
          <Sparkles className="w-4 h-4 text-[#FF6B35]" />
          <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#FF6B35]">{c.eyebrow}</p>
        </div>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#0F172A] leading-[1.1] tracking-tight mb-5 max-w-4xl">
          {c.title}
        </h2>
        <p className="text-base md:text-lg text-[#475569] leading-relaxed max-w-3xl font-light mb-12">
          {c.lede}
        </p>

        {/* Advantages */}
        <div className="mb-16">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#00B4D8] mb-5">{c.advantages_title}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {c.advantages.map((a, i) => (
              <div key={i} className="rounded-xl bg-white border border-gray-200 p-5 hover:border-[#FF6B35]/50 hover:shadow-md transition">
                <a.icon className="w-6 h-6 text-[#FF6B35] mb-3" />
                <div className="text-base font-extrabold text-[#0F172A] mb-1">{a.h}</div>
                <div className="text-sm text-[#475569] leading-relaxed">{a.p}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Comparison */}
        <div className="mb-16">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#00B4D8] mb-5">{c.compare_title}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {c.compare.map((col, i) => (
              <div
                key={i}
                className={`rounded-2xl p-6 border-2 ${
                  col.best
                    ? 'bg-gradient-to-br from-[#0F172A] to-[#1E293B] border-[#FF6B35] text-white shadow-xl'
                    : 'bg-white border-gray-200 text-[#0F172A]'
                }`}
              >
                <div className={`text-sm font-extrabold mb-3 ${col.best ? 'text-amber-300 uppercase tracking-widest' : 'text-[#0F172A]'}`}>{col.col}</div>
                <ul className="space-y-2">
                  {col.items.map((it, j) => (
                    <li key={j} className={`text-sm flex items-start gap-2 ${col.best ? 'text-white/90' : col.bad ? 'text-[#475569]' : ''}`}>
                      {col.best ? (
                        <CheckCircle2 className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
                      ) : (
                        <span className="text-[#475569] font-mono text-[11px] mt-0.5">·</span>
                      )}
                      {it}
                    </li>
                  ))}
                </ul>
                {col.best && (
                  <div className="mt-4 inline-flex items-center gap-1 bg-amber-300 text-[#0F172A] text-[10px] font-extrabold uppercase tracking-widest px-2 py-1 rounded">
                    {c.best_value}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Plans */}
        <div className="mb-16">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#00B4D8] mb-5">{c.plans_title}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {c.plans.map((p, i) => (
              <div
                key={i}
                className={`rounded-2xl p-6 border ${
                  p.featured
                    ? 'bg-gradient-to-br from-white to-slate-50 border-[#FF6B35] shadow-xl scale-[1.02]'
                    : 'bg-white border-gray-200'
                }`}
              >
                {p.featured && (
                  <div className="inline-flex bg-[#FF6B35] text-white text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded mb-2">{c.most_popular}</div>
                )}
                <div className="text-base font-extrabold text-[#0F172A]">{p.name}</div>
                <div className="text-2xl font-black text-[#0F172A] mt-1">{p.price}</div>
                <div className="text-xs text-[#94A3B8] mt-1 mb-3">{p.sub}</div>
                <ul className="space-y-1.5 mb-4">
                  {p.list.map((l, j) => (
                    <li key={j} className="flex items-start gap-2 text-xs text-[#475569]">
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#00B4D8] flex-shrink-0 mt-0.5" />
                      {l}
                    </li>
                  ))}
                </ul>
                <Link to="/brand-studio">
                  <button className={`w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold ${
                    p.featured ? 'bg-[#FF6B35] hover:bg-[#e85a25] text-white' : 'bg-[#0F172A] hover:bg-[#1E293B] text-white'
                  }`}>
                    {p.featured ? c.plan_cta_featured : c.plan_cta_default} <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* Closing CTA with inline lead form */}
        <div className="relative rounded-3xl bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] p-8 md:p-12 overflow-hidden">
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-[#FF6B35]/25 rounded-full blur-3xl" />
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-300/10 border border-amber-300/30 text-amber-300 text-[11px] font-bold uppercase tracking-[0.2em] mb-4">
                <Sparkles className="w-3 h-3" /> {c.closing_eyebrow}
              </div>
              <h3 className="text-2xl md:text-3xl font-extrabold text-white leading-tight mb-4 max-w-xl">
                {c.closing_title}
              </h3>
              <p className="text-base text-white/70 mb-7 font-light">{c.closing_body}</p>
              <div className="flex flex-wrap items-center gap-3">
                <Link to="/brand-studio">
                  <button
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/10 hover:bg-white/15 border border-white/20 text-white font-bold transition"
                    data-testid="insights-brand-studio-cta"
                  >
                    {c.cta_primary} <ArrowRight className="w-4 h-4" />
                  </button>
                </Link>
                <Link to="/schedule" className="text-sm text-white/80 hover:text-white underline underline-offset-4">
                  {c.cta_secondary}
                </Link>
              </div>
            </div>

            {/* Inline lead-magnet form */}
            <BrandStudioLeadForm source="insights_article" variant="dark" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedBrandStudioArticle;
